import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";

import { Icon, IconType } from "components/_icons/Icon";
import { Checkbox } from "components/_inputs/Checkbox";

import { CheckboxCheckedAppearance } from "ts/enums/checkboxCheckedAppearance";
import { Color } from "ts/enums/color";

import { isAllSelected } from "utils/userDemographics";
import { TextTruncator } from "components/TextTruncator";

export type ChecklistItem = {
  name: string;
  values: string[];
};

type Props = {
  id: number;
  checklistItem: ChecklistItem;
  selectedValues: ChecklistItem[];
  parentCheckboxLabel?: string;
  isExpandable?: boolean;
  isMenuClosed?: boolean;
  hasTooltip?: boolean;
  updateValues: (updatedItem: ChecklistItem) => void;
};

export const NestedChecklist = ({
  id,
  checklistItem,
  selectedValues,
  parentCheckboxLabel,
  isExpandable = true,
  isMenuClosed,
  hasTooltip = true,
  updateValues,
}: Props) => {
  const [showChildren, setShowChildren] = useState<boolean>(false);

  const currentlySelectedItem = selectedValues.find((sv) => sv.name === checklistItem.name);

  const getCheckboxVariant = (value?: string): CheckboxCheckedAppearance => {
    if (!currentlySelectedItem) return CheckboxCheckedAppearance.Default;
    const variantCondition = value
      ? checklistItem.values.includes(value)
      : isAllSelected(checklistItem.values, currentlySelectedItem.values);
    return variantCondition ? CheckboxCheckedAppearance.Default : CheckboxCheckedAppearance.Partial;
  };

  useEffect(() => {
    if (isMenuClosed) setShowChildren(false);
  }, [isMenuClosed]);

  return (
    <>
      <StyledNestedChecklistContainer key={`${id}-${checklistItem.name}`}>
        {isExpandable && (
          <StyledIconContainer
            hasChildren={!!checklistItem.values}
            onClick={() => setShowChildren((show) => !show)}
          >
            <Icon
              type={showChildren ? IconType.minus : IconType.plus}
              size={14}
              color={Color.gray30}
            />
          </StyledIconContainer>
        )}
        <StyledCheckboxContainer hasTooltip={hasTooltip}>
          <Checkbox
            checked={currentlySelectedItem?.values.length > 0}
            checkedAppearance={getCheckboxVariant()}
            onChange={() => updateValues(checklistItem)}
          />
          <span onClick={() => updateValues(checklistItem)}>
            {hasTooltip ? (
              <TextTruncator id={id} value={parentCheckboxLabel ?? checklistItem.name} />
            ) : (
              parentCheckboxLabel ?? checklistItem.name
            )}
          </span>
        </StyledCheckboxContainer>
      </StyledNestedChecklistContainer>
      {(!isExpandable || showChildren) && (
        <StyledChildrenContainer showExpand={isExpandable}>
          {checklistItem.values.map((v, index) => (
            <StyledCheckboxContainer key={`${v}-${index}`} hasTooltip={hasTooltip}>
              <Checkbox
                checked={currentlySelectedItem?.values.includes(v) ?? false}
                checkedAppearance={getCheckboxVariant(v)}
                onChange={() => updateValues({ name: checklistItem.name, values: [v] })}
              />
              <span onClick={() => updateValues({ name: checklistItem.name, values: [v] })}>
                {hasTooltip ? <TextTruncator id={index} value={v} /> : v}
              </span>
            </StyledCheckboxContainer>
          ))}
        </StyledChildrenContainer>
      )}
    </>
  );
};

const StyledNestedChecklistContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const StyledIconContainer = styled.div<{ hasChildren: boolean }>`
  cursor: ${({ hasChildren }) => (hasChildren ? "pointer" : "default")};
  visibility: ${({ hasChildren }) => (hasChildren ? "visible" : "hidden")};
`;

const StyledCheckboxContainer = styled.div<{ hasTooltip: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;

  span:nth-child(2) {
    ${({ hasTooltip }) =>
      hasTooltip &&
      css`
        text-overflow: ellipsis;
        overflow: hidden;
        max-lines: 1;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
      `}
    font-size: 0.875em;
    cursor: pointer;
    word-break: break-all;
  }
`;

const StyledChildrenContainer = styled.div<{ showExpand: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: ${({ showExpand }) => (showExpand ? "2.5em" : 0)};
`;
